import pmConstraintService from '@/services/pmConstraintService';

export default {
  namespaced: true,

  state: {
    constraints: [],
    total: 0,
    projectId: null,
    loading: false,
    error: null,
    summary: null,
    summaryLoading: false,
    summaryError: null,
    summaryRequestId: 0,
  },

  getters: {
    constraints: (state) => state.constraints,
    total: (state) => state.total,
    projectId: (state) => state.projectId,
    loading: (state) => state.loading,
    error: (state) => state.error,
    isEmpty: (state) => !state.loading && !state.error && state.constraints.length === 0,
    hasProjectContext: (state) => Boolean(state.projectId),
    openConstraints: (state) => state.constraints.filter(
      (item) => item.status === 'open' || item.status === 'in_progress',
    ),
    internalOpenCount: (state, getters) => (
      getters.openConstraints.filter((item) => item.type === 'intern').length
    ),
    crossOpenCount: (state, getters) => (
      getters.openConstraints.filter((item) => item.type === 'bereichsuebergreifend').length
    ),
    summary: (state) => state.summary,
    summaryLoading: (state) => state.summaryLoading,
    summaryError: (state) => state.summaryError,
  },

  mutations: {
    SET_PROJECT_ID(state, projectId) {
      state.projectId = projectId;
    },
    SET_LOADING(state, loading) {
      state.loading = loading;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    SET_CONSTRAINTS(state, { items, total }) {
      state.constraints = items || [];
      state.total = typeof total === 'number' ? total : state.constraints.length;
    },
    SET_SUMMARY_LOADING(state, loading) {
      state.summaryLoading = loading;
    },
    SET_SUMMARY_ERROR(state, error) {
      state.summaryError = error;
    },
    SET_SUMMARY(state, summary) {
      state.summary = summary;
    },
    SET_SUMMARY_REQUEST_ID(state, requestId) {
      state.summaryRequestId = requestId;
    },
    ADD_CONSTRAINT(state, constraint) {
      state.constraints.unshift(constraint);
      state.total += 1;
    },
    UPDATE_CONSTRAINT(state, updated) {
      const index = state.constraints.findIndex((c) => c.id === updated.id);
      if (index !== -1) {
        state.constraints.splice(index, 1, updated);
      }
    },
    REMOVE_CONSTRAINT(state, id) {
      state.constraints = state.constraints.filter((c) => c.id !== id);
      state.total = Math.max(0, state.total - 1);
    },
    RESET(state) {
      state.constraints = [];
      state.total = 0;
      state.projectId = null;
      state.loading = false;
      state.error = null;
      state.summary = null;
      state.summaryLoading = false;
      state.summaryError = null;
      state.summaryRequestId = (state.summaryRequestId || 0) + 1;
    },
  },

  actions: {
    setProjectContext({ commit, dispatch }, projectId) {
      commit('SET_PROJECT_ID', projectId);
      if (projectId) {
        return Promise.all([
          dispatch('fetchConstraints'),
          dispatch('fetchConstraintsSummary'),
        ]);
      }
      commit('SET_CONSTRAINTS', { items: [], total: 0 });
      commit('SET_SUMMARY', null);
      return Promise.resolve();
    },

    async fetchConstraints({ commit, state }) {
      if (!state.projectId) {
        commit('SET_CONSTRAINTS', { items: [], total: 0 });
        return;
      }
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      try {
        const data = await pmConstraintService.listConstraints(state.projectId);
        commit('SET_CONSTRAINTS', {
          items: data?.items || [],
          total: data?.total ?? 0,
        });
      } catch (error) {
        const message = error.response?.data?.message || error.message || 'Failed to load constraints';
        commit('SET_ERROR', message);
        commit('SET_CONSTRAINTS', { items: [], total: 0 });
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async fetchConstraintsSummary({ commit, state, rootGetters }) {
      const projectId = state.projectId || rootGetters['pmProject/selectedProjectId'];
      if (!projectId) {
        commit('SET_SUMMARY', null);
        return null;
      }
      const requestId = (state.summaryRequestId || 0) + 1;
      commit('SET_SUMMARY_REQUEST_ID', requestId);
      commit('SET_SUMMARY_LOADING', true);
      commit('SET_SUMMARY_ERROR', null);
      try {
        const summary = await pmConstraintService.getConstraintsSummary(projectId);
        // Ignore stale responses when project context changed mid-flight.
        if (state.summaryRequestId !== requestId) {
          return null;
        }
        if ((state.projectId || rootGetters['pmProject/selectedProjectId']) !== projectId) {
          return null;
        }
        commit('SET_SUMMARY', summary);
        return summary;
      } catch (error) {
        if (state.summaryRequestId !== requestId) {
          return null;
        }
        const message = error.response?.data?.message || error.message || 'Failed to load constraint summary';
        commit('SET_SUMMARY_ERROR', message);
        commit('SET_SUMMARY', null);
        throw error;
      } finally {
        if (state.summaryRequestId === requestId) {
          commit('SET_SUMMARY_LOADING', false);
        }
      }
    },

    async createConstraint({ commit, state }, payload) {
      commit('SET_ERROR', null);
      try {
        const constraint = await pmConstraintService.createConstraint(state.projectId, payload);
        commit('ADD_CONSTRAINT', constraint);
        return constraint;
      } catch (error) {
        const message = error.response?.data?.message || error.message || 'Failed to create constraint';
        commit('SET_ERROR', message);
        throw error;
      }
    },

    async updateConstraint({ commit, state }, { id, payload }) {
      commit('SET_ERROR', null);
      try {
        const constraint = await pmConstraintService.updateConstraint(state.projectId, id, payload);
        commit('UPDATE_CONSTRAINT', constraint);
        return constraint;
      } catch (error) {
        const message = error.response?.data?.message || error.message || 'Failed to update constraint';
        commit('SET_ERROR', message);
        throw error;
      }
    },

    async resolveConstraint({ commit, state }, id) {
      commit('SET_ERROR', null);
      try {
        const constraint = await pmConstraintService.resolveConstraint(state.projectId, id);
        commit('UPDATE_CONSTRAINT', constraint);
        return constraint;
      } catch (error) {
        const message = error.response?.data?.message || error.message || 'Failed to resolve constraint';
        commit('SET_ERROR', message);
        throw error;
      }
    },

    async deleteConstraint({ commit, state }, id) {
      commit('SET_ERROR', null);
      try {
        await pmConstraintService.deleteConstraint(state.projectId, id);
        commit('REMOVE_CONSTRAINT', id);
      } catch (error) {
        const message = error.response?.data?.message || error.message || 'Failed to delete constraint';
        commit('SET_ERROR', message);
        throw error;
      }
    },
  },
};
